// Strona pojedynczego artykułu z bazy wiedzy

import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { reduxActions, useDispatch } from 'store';
import { useGetKnowledgeBaseArticle } from 'api';
import { IKnowledgeBaseArticle } from 'api/endpoints/useGetKnowledgeBaseArticles';
import { Container, Breadcrumbs, Loader } from 'components/controls';
import { CalendarIcon, TagIcon } from 'assets/icons';
import { generateKnowledgeBaseArticlePageScheme } from 'api/helpers';

import styles from 'theme/pages/KnowledgeBaseArticle/KnowledgeBaseArticle.module.scss';

const KnowledgeBaseArticle = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const articleId = useMemo(() => {
    const parsed = parseInt(id || '', 10);
    return isNaN(parsed) ? 0 : parsed;
  }, [id]);

  // Pobranie pojedynczego artykułu
  const { data: article, isLoading } = useGetKnowledgeBaseArticle(articleId);

  // Generowanie JSON-LD schema dla artykułu z bazy wiedzy
  const articlePageScheme = useMemo(() => {
    if (!article) return null;
    return generateKnowledgeBaseArticlePageScheme(article, window.location.href);
  }, [article]);

  // Funkcja pomocnicza do wyciągania danych z article_fields
  const getArticleData = (article: IKnowledgeBaseArticle) => {
    // Znajdź pole z obrazem (section_field_symbol === "image") - tylko do wyciągnięcia daty
    const imageField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'image' && field.file
    );

    // Znajdź pole z podtytułem (section_field_symbol === "Podtytuł")
    const subtitleField = article.article_fields?.find(
      (field) => field.section_field_symbol === 'Podtytuł'
    );

    // Znajdź pole z treścią (section_field_symbol === "content" lub field_name === "content")
    const contentField = article.article_fields?.find(
      (field) =>
        field.section_field_symbol === 'content' || field.field_name === 'content'
    );

    const subtitleFromArticle = article.subtitle || '';
    const contentFromArticle = article.content || '';
    let date = '';

    // Wyciągnij datę z pola obrazu (jeśli istnieje)
    if (imageField?.file) {
      const translations = imageField.file.translations || imageField.file.iterator || [];
      const currentLangTranslation =
        translations.find((t) => t.language === i18n.language) || translations[0];

      // Backend zwraca już sformatowaną datę z uwzględnieniem locale
      if (currentLangTranslation?.sys_insert_date) {
        date = currentLangTranslation.sys_insert_date;
      }
    }

    // Pobierz podtytuł z pola (jeśli nie ma w article.subtitle)
    const subtitleValue = subtitleField && typeof subtitleField.value === 'string' ? subtitleField.value : '';
    const subtitle = subtitleFromArticle || subtitleValue;

    // Pobierz treść z pola (jeśli nie ma w article.content)
    const contentValue = contentField && typeof contentField.value === 'string' ? contentField.value : '';
    const content = contentFromArticle || contentValue;

    return { subtitle, content, date };
  };

  // Ustawienie breadcrumbs'ów
  useEffect(() => {
    if (article) {
      dispatch(
        reduxActions.setBreadcrumbs([
          {
            name: t('Baza wiedzy'),
            path: '/knowledge-base'
          },
          {
            name: article.title || '',
            path: undefined
          }
        ])
      );
    }
  }, [article]);

  if (isLoading) {
    return (
      <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseArticle')}>
        <Loader />
      </div>
    );
  }

  if (!article) {
    return (
      <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseArticle')}>
        <Container>
          <div>{t('Artykuł nie został znaleziony')}</div>
        </Container>
      </div>
    );
  }

  const { subtitle, content, date } = getArticleData(article);

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseArticle')}>
      {article && (
        <Helmet>
          <title>{article.title}</title>
          <link rel="canonical" href={window.location.href} />
          {articlePageScheme && (
            articlePageScheme.map((scheme, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(scheme)}
              </script>
            ))
          )}
        </Helmet>
      )}
      <Breadcrumbs />

      <Container>
        <div className={styles.articleContent}>
          <h1 className={styles.title}>{article.title}</h1>

          <div className={styles.lineSeparator} />

          <div className={styles.meta}>
            <div className={styles.tags}>
              <TagIcon className={styles.tagsIcon} />
              <span className={styles.tagsLabel}>TAGI:</span>
            </div>

            {date && (
              <span className={styles.date}>
                <CalendarIcon className={styles.calendarIcon} />
                {date}
              </span>
            )}
          </div>

          {subtitle && <h2 className={styles.subtitle} dangerouslySetInnerHTML={{ __html: subtitle }} />}

          <article className={styles.article}>
            <div className={styles.contentWrapper}>
              {content && (
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: content }} />
              )}
            </div>
          </article>
        </div>
      </Container>
    </div>
  );
};

export default KnowledgeBaseArticle;
